import { backlitFace, haloPct, isDimmed, pressedSurface } from "@/render/board-compose/cap-face";
import { LensMark } from "@/render/components/LensMark";
import type { CapStyle } from "@/render/tweaks/use-tweaks";
import type { ChipReservation, RenderZone } from "./compose";
import {
  CHANNEL_LEFT,
  CHIP_RESERVE,
  CHIP_STUB,
  MOUSE_CHIPS,
  MOUSE_FRAME,
  WHEEL_WRAP,
} from "./geometry";

interface MouseMapProps {
  zones: RenderZone[];
  /** The shared key unit; the mouse scales with the board (design v2 section 5). */
  unit: number;
  /** Chip columns held open per Profile; layout never shifts. */
  reserve: ChipReservation;
  /** Cap style; backlit gives zones the same dark RGB-lit treatment as keycaps. */
  keycapStyle?: CapStyle;
  /** Backlight strength 0-1.5; scales the backlit halo. */
  keyGlow?: number;
  /** Whether bound zones print their binding name (mirrors the keyboard's tweak). */
  showNames?: boolean;
  /** Category ids kept lit while isolating; bound zones outside them dim. */
  dimCategories?: string[] | null;
  /** The search/hover flashlight: when set, every zone whose code is NOT listed dims. */
  litCodes?: string[] | null;
  /** While a non-empty combo is held, dim base-fallback zones. */
  dimFallback?: boolean;
  /** Zone codes pressed (depressed) - the press-feedback transient. */
  pressedCodes?: string[] | null;
}

/** Per-zone face colours: backlit paints a dark lit zone, flat tints the light one. */
function zoneFace(
  z: RenderZone,
  backlit: boolean,
  kg: number,
  u: number,
  dimmed: boolean,
): { background?: string; color?: string; borderColor?: string; boxShadow?: string } {
  const c = z.colors;
  if (backlit) {
    const face = backlitFace(!!z.binding, c);
    if (z.binding && c) {
      const pct = haloPct(kg);
      const halo =
        pct > 0 && !dimmed
          ? `, 0 0 ${Math.max(6, Math.round(u * 0.3 * kg))}px color-mix(in oklch, ${c.glow} ${pct}%, transparent)`
          : "";
      return {
        background: face.surface,
        color: face.legend,
        borderColor: `color-mix(in oklch, ${c.glow} 30%, rgba(0,0,0,.38))`,
        boxShadow: `inset 0 1px 0 rgba(255,255,255,.09)${halo}`,
      };
    }
    return {
      background: face.surface,
      color: face.legend,
      borderColor: "rgba(0,0,0,.34)",
      boxShadow: "inset 0 1px 0 rgba(255,255,255,.09)",
    };
  }
  return {
    background: c?.tint,
    color: c?.text,
    borderColor: z.binding ? "rgba(15, 18, 28, 0.14)" : undefined,
  };
}

/** Per-zone corner radii (prototype values); presentation, not section 5 data. */
function zoneRadius(code: string, u: number): string {
  switch (code) {
    case "MouseLeft":
      return `0 ${0.16 * u}px ${0.16 * u}px ${0.3 * u}px`;
    case "MouseRight":
      return `${0.16 * u}px 0 ${0.3 * u}px ${0.16 * u}px`;
    case "WheelUp":
      return `${0.24 * u}px ${0.24 * u}px ${0.1 * u}px ${0.1 * u}px`;
    case "WheelDown":
      return `${0.1 * u}px ${0.1 * u}px ${0.24 * u}px ${0.24 * u}px`;
    case "MouseMiddle":
      return `${0.14 * u}px`;
    default: // the thumb buttons
      return `${0.24 * u}px ${0.08 * u}px ${0.08 * u}px ${0.24 * u}px`;
  }
}

/**
 * The mouse map (design v2 section 5): a top-down mouse whose zones behave exactly
 * like keycaps - category coloring, isolate dimming, list-hover flashlight, click
 * readout. LMB/RMB are flush shells clipped to the body silhouette with the outline
 * redrawn on top; the wheel sits in a recessed groove; the palm carries the lens
 * mark in ember. Bound small zones get annotation chips beside the mouse.
 */
export function MouseMap({
  zones,
  unit: u,
  reserve,
  keycapStyle = "backlit",
  keyGlow = 0.85,
  showNames = true,
  dimCategories = null,
  litCodes = null,
  dimFallback = false,
  pressedCodes = null,
}: MouseMapProps) {
  const backlit = keycapStyle === "backlit";
  const kg = Math.max(0, keyGlow);

  const W = MOUSE_FRAME.width * u;
  const H = MOUSE_FRAME.height * u;
  const inset = MOUSE_FRAME.bodyInset * u;
  const LW = reserve.left ? CHIP_RESERVE.left * u : 0;
  const RW = reserve.right ? CHIP_RESERVE.right * u : 0;
  const bodyBR = `${1.9 * u}px ${1.9 * u}px ${1.75 * u}px ${1.75 * u}px / ${2.25 * u}px ${2.25 * u}px ${2.5 * u}px ${2.5 * u}px`;
  // groove centre x (absolute), where the wrapping wheel-chip connectors enter the wheel column
  const grooveX = LW + (CHANNEL_LEFT + MOUSE_FRAME.channelWidth / 2) * u;
  // the wheel-chip pills line up with the straight MMB pill, whose 0.36u stub offsets it from the
  // mouse edge by the same amount; the wrapping connector ends at this shared left edge
  const chipWire = CHIP_STUB * u;
  const wheelPillLeft = LW + W + chipWire;

  const byCode = new Map(zones.map((z) => [z.code, z]));

  // the dim rule is shared with the board (cap-face.isDimmed): the flashlight spotlights its
  // matches and dims the rest; with none, an isolated Category or a held-combo base fallback
  // dims. A pressed zone is full-strength - its depress is the focus - and its chip, connector,
  // and pill follow the zone, so one pressed-aware rule serves all four render sites.
  const isDim = (z: RenderZone) =>
    !(pressedCodes?.includes(z.code) ?? false) &&
    isDimmed(z, { dimCategories, litCodes, dimFallback });

  const renderZone = (z: RenderZone, offsetX: number) => {
    const pressed = pressedCodes?.includes(z.code) ?? false;
    const dimmed = isDim(z);
    const classes = ["mz"];
    if (dimmed) classes.push("mz-dim");
    if (pressed) classes.push("mz-press");
    const face = zoneFace(z, backlit, kg, u, dimmed);
    return (
      // the mouse is driven by the player's real mouse buttons (useMouseButtons) and the
      // wheel, not by clicking zones - so a zone is a static, non-interactive graphic
      <div
        key={z.code}
        data-zone={z.code}
        className={classes.join(" ")}
        title={z.binding ? z.binding.name : z.legend}
        style={{
          left: z.x * u - offsetX,
          top: z.y * u,
          width: z.w * u,
          height: z.h * u,
          gap: Math.max(1, u * 0.04),
          borderRadius: zoneRadius(z.code, u),
          background: pressed ? pressedSurface(z.colors?.accent, backlit) : face.background,
          borderColor: face.borderColor,
          color: pressed ? "#fff" : face.color,
          boxShadow: pressed
            ? backlit
              ? "inset 0 2px 5px rgba(0,0,0,.5)"
              : "inset 0 2px 3px rgba(15,18,28,.20)"
            : face.boxShadow,
          transform: pressed ? `translateY(${Math.max(2, Math.round(u * 0.1) - 1)}px)` : undefined,
        }}
      >
        {z.code === "MouseMiddle" ? <span className="mm-notches" /> : null}
        <span
          className="mz-label"
          style={{ fontSize: Math.max(8, u * 0.16), opacity: z.binding ? 0.75 : 1 }}
        >
          {z.label}
        </span>
        {z.binding && !z.small && showNames ? (
          <span className="mz-name" style={{ fontSize: Math.max(8, u * 0.19) }}>
            {z.binding.name}
          </span>
        ) : null}
      </div>
    );
  };

  // an annotation pill: a small zone's binding name, beside the mouse (shared by the straight
  // thumb/MMB chips and the wrapping wheel chips)
  const chipPill = (z: RenderZone) =>
    z.binding && z.colors ? (
      <span
        className="mm-chip-pill"
        title={z.binding.name}
        style={{
          gap: Math.max(4, u * 0.1),
          padding: `${Math.max(2, u * 0.07)}px ${Math.max(5, u * 0.16)}px`,
          borderRadius: Math.max(5, u * 0.14),
          background: backlit
            ? `color-mix(in oklch, ${z.colors.glow} 14%, oklch(0.32 0.012 261))`
            : z.colors.tint,
          color: backlit ? z.colors.lit : z.colors.text,
          borderColor: backlit
            ? `color-mix(in oklch, ${z.colors.glow} 32%, rgba(0,0,0,.35))`
            : undefined,
          boxShadow: backlit ? "inset 0 1px 0 rgba(255,255,255,.07)" : undefined,
        }}
      >
        <span className="mm-chip-name" style={{ fontSize: Math.max(9, u * 0.2) }}>
          {z.binding.name}
        </span>
      </span>
    ) : null;

  return (
    <div className="mm" style={{ width: LW + W + RW, height: H }} data-testid="mouse-map">
      <div
        className="mm-body"
        style={{ left: LW + inset, width: W - inset, height: H, borderRadius: bodyBR }}
      />
      {/* split line between the button shells and the palm */}
      <div
        className="mm-split"
        style={{
          left: LW + inset + 0.12 * u,
          right: RW + 0.12 * u,
          top: MOUSE_FRAME.splitLine * u,
        }}
      />
      {/* palm logo: the lens mark, quiet ember */}
      <div
        className="mm-mark"
        style={{
          left: LW + inset + (W - inset) / 2 - 0.42 * u,
          top: 4.25 * u,
          width: 0.84 * u,
          height: 0.84 * u,
        }}
      >
        <LensMark />
      </div>
      {/* shells and wheel, clipped to the body silhouette so nothing overhangs the curve */}
      <div
        className="mm-clip"
        style={{ left: LW + inset, width: W - inset, height: H, borderRadius: bodyBR }}
      >
        {/* recessed groove the wheel sits in */}
        <div
          className="mm-groove"
          style={{
            left: CHANNEL_LEFT * u - inset,
            width: MOUSE_FRAME.channelWidth * u,
            height: MOUSE_FRAME.buttonsEnd * u,
            borderRadius: `0 0 ${0.2 * u}px ${0.2 * u}px`,
          }}
        />
        {zones.filter((z) => z.clipped).map((z) => renderZone(z, inset))}
        {/* redraw the body outline over the flush shells */}
        <div className="mm-edge" style={{ borderRadius: bodyBR }} />
      </div>
      {/* thumb side-buttons protrude past the body's left edge */}
      {zones.filter((z) => !z.clipped).map((z) => renderZone(z, -LW))}
      {/* straight chips: thumb buttons (M4/M5) and the wheel press (MMB). The wheel scroll
          chips route differently (WHEEL_WRAP, below), so MOUSE_CHIPS holds only these. */}
      {MOUSE_CHIPS.map((chip) => {
        const z = byCode.get(chip.code);
        if (!z?.binding || !z.colors) return null;
        const classes = ["mm-chip"];
        if (isDim(z)) classes.push("mm-chip-dim");
        const anchor: React.CSSProperties = { top: chip.cy * u };
        if (chip.side === "left") {
          anchor.right = W + RW;
          anchor.flexDirection = "row-reverse";
        } else {
          anchor.left = LW + W;
        }
        return (
          <div key={chip.code} className={classes.join(" ")} style={anchor}>
            <span
              className="mm-wire"
              style={{
                width: chipWire,
                background: `color-mix(in oklch, ${backlit ? z.colors.glow : z.colors.accent} 50%, transparent)`,
              }}
            />
            {chipPill(z)}
          </div>
        );
      })}
      {/* wheel scroll chips: their zones sit in the central groove, so the connector wraps the
          RMB (over the top for Scroll Up, under the bottom for Scroll Down) to reach the wheel
          column - making clear the pill names the wheel and not the RMB the old stub pointed at. */}
      <svg
        className="mm-wrap"
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          width: LW + W + RW,
          height: H,
          overflow: "visible",
          pointerEvents: "none",
          zIndex: 2,
        }}
      >
        {WHEEL_WRAP.map(({ code, zoneY, runY }) => {
          const z = byCode.get(code);
          if (!z?.binding || !z.colors) return null;
          return (
            <polyline
              key={code}
              points={`${grooveX},${zoneY * u} ${grooveX},${runY * u} ${wheelPillLeft},${runY * u}`}
              fill="none"
              strokeWidth={1.5}
              stroke={`color-mix(in oklch, ${backlit ? z.colors.glow : z.colors.accent} 55%, transparent)`}
              opacity={isDim(z) ? 0.38 : 1}
            />
          );
        })}
      </svg>
      {WHEEL_WRAP.map(({ code, runY }) => {
        const z = byCode.get(code);
        if (!z?.binding || !z.colors) return null;
        return (
          <div
            key={code}
            className={`mm-chip${isDim(z) ? " mm-chip-dim" : ""}`}
            style={{ left: wheelPillLeft, top: runY * u, transform: "translateY(-50%)" }}
          >
            {chipPill(z)}
          </div>
        );
      })}
    </div>
  );
}
